/**
 * avatars/animals/build-avian.js
 * Xây dựng cây phân cấp 3D cho Gia cầm 2 chân (Avian Archetype: Gà & Vịt)
 * Bỏ cổ, đầu gắn trực tiếp vào thân như Figurine, hỗ trợ mỏ dẹt, chân màng, mào & tích.
 */

import * as THREE from 'three';
import { createAnimalMaterials } from './animal-materials.js';
import { buildEyes, buildBlush, buildAvianBeak, buildAvianFoot } from './animal-parts.js';

export function buildAvian(root, morph, variant, parts, disposables) {
  const m = morph;
  const v = variant;

  // Khởi tạo materials phân vùng (lông vũ, mỏ, mào tích, chân, cánh, đuôi)
  const mats = createAnimalMaterials(m, v, disposables, true);

  // --- 1. Thân mình (Body) ---
  const bodyY = m.legs.height + m.body.height / 2;
  const bodyGroup = new THREE.Group();
  bodyGroup.name = 'BodyGroup';
  bodyGroup.position.set(0, bodyY, 0);
  root.add(bodyGroup);
  parts.body = bodyGroup;

  const bodyGeo = new THREE.BoxGeometry(m.body.width, m.body.height, m.body.length);
  const bodyMesh = new THREE.Mesh(bodyGeo, mats.bodyMaterials);
  bodyMesh.castShadow = false;
  bodyMesh.receiveShadow = true;
  bodyGroup.add(bodyMesh);
  disposables.push(bodyGeo);

  // --- 2. Đầu (Head) - Gắn trực tiếp vào thân mình ---
  const headGroup = new THREE.Group();
  headGroup.name = 'HeadGroup';
  headGroup.position.set(0, m.body.height * 0.22, m.body.length * 0.32);
  bodyGroup.add(headGroup);
  parts.head = headGroup;
  parts.baseHeadZ = headGroup.position.z;

  const headGeo = new THREE.BoxGeometry(m.head.sizeX, m.head.sizeY, m.head.sizeZ);
  const headMesh = new THREE.Mesh(headGeo, mats.headMaterials);
  headMesh.position.set(0, m.head.sizeY * 0.32, m.head.sizeZ * 0.25);
  headGroup.add(headMesh);
  disposables.push(headGeo);

  // --- 3. Mỏ 3D (Beak) ---
  buildAvianBeak(headGroup, m, mats, disposables);

  // --- 4. Mắt & Má hồng ---
  buildEyes(headGroup, m, v, mats, disposables, true);
  buildBlush(headGroup, m, mats, disposables, true);

  // --- 5. Mào & Tích đỏ cho Gà (Comb & Wattle) ---
  if (m.comb && m.comb.hasComb) {
    const combGeo = new THREE.BoxGeometry(m.comb.width, m.comb.height, m.comb.length);
    const combMesh = new THREE.Mesh(combGeo, mats.combMat);
    combMesh.position.set(0, m.head.sizeY * 0.32 + m.head.sizeY / 2 + m.comb.height / 2, m.head.sizeZ * 0.25);
    headGroup.add(combMesh);
    disposables.push(combGeo);
  }

  if (m.wattle && m.wattle.hasWattle) {
    const wattleGeo = new THREE.BoxGeometry(m.wattle.width, m.wattle.height, m.wattle.length);
    const wattleMesh = new THREE.Mesh(wattleGeo, mats.combMat);
    wattleMesh.position.set(0, m.head.sizeY * 0.12, m.head.sizeZ * 0.25 + m.head.sizeZ / 2);
    headGroup.add(wattleMesh);
    disposables.push(wattleGeo);
  }

  // --- 6. Hai Cánh (Wings) ---
  const wingGeo = new THREE.BoxGeometry(m.wings.thickness, m.wings.height, m.wings.length);
  disposables.push(wingGeo);

  const leftWing = new THREE.Group();
  leftWing.name = 'LeftWing';
  leftWing.position.set(-m.body.width / 2 - m.wings.thickness / 2, m.body.height * 0.2, 0);
  const lWingMesh = new THREE.Mesh(wingGeo, mats.wingMat);
  lWingMesh.position.y = -m.wings.height / 2;
  leftWing.add(lWingMesh);
  bodyGroup.add(leftWing);
  parts.leftWing = leftWing;

  const rightWing = new THREE.Group();
  rightWing.name = 'RightWing';
  rightWing.position.set(m.body.width / 2 + m.wings.thickness / 2, m.body.height * 0.2, 0);
  const rWingMesh = new THREE.Mesh(wingGeo, mats.wingMat);
  rWingMesh.position.y = -m.wings.height / 2;
  rightWing.add(rWingMesh);
  bodyGroup.add(rightWing);
  parts.rightWing = rightWing;

  // --- 7. Hai Chân & Bàn chân (Stubby Feet / Webbed Paddle) ---
  const legH = m.legs.height;
  const legGeo = new THREE.BoxGeometry(m.legs.width, legH, m.legs.depth);
  disposables.push(legGeo);

  const createAvianLeg = (name, x) => {
    const legJoint = new THREE.Group();
    legJoint.name = name;
    legJoint.position.set(x, -m.body.height / 2, 0);

    const legMesh = new THREE.Mesh(legGeo, mats.legMat);
    legMesh.position.y = -legH / 2;
    legJoint.add(legMesh);

    buildAvianFoot(legJoint, legH, m, mats, disposables);

    bodyGroup.add(legJoint);
    return legJoint;
  };

  parts.leftLeg = createAvianLeg('LeftLeg', -m.legs.spacingX / 2);
  parts.rightLeg = createAvianLeg('RightLeg', m.legs.spacingX / 2);

  // --- 8. Đuôi Lông (Tail) ---
  const tailGroup = new THREE.Group();
  tailGroup.name = 'TailGroup';
  tailGroup.position.set(0, m.body.height * 0.2, -m.body.length / 2 + 0.02);
  bodyGroup.add(tailGroup);
  parts.tail = tailGroup;

  const tailGeo = new THREE.BoxGeometry(m.tail.width, m.tail.height, m.tail.length);
  const tailMesh = new THREE.Mesh(tailGeo, mats.tailMat);
  tailMesh.position.set(0, m.tail.height * 0.4, -m.tail.length * 0.4);
  tailMesh.rotation.x = m.tail.angle || 0.6;
  tailGroup.add(tailMesh);
  disposables.push(tailGeo);
}
